/**
 * The base toolset connection (wayai-pro/wayai#5083, Track K): what WayAI reads from the data
 * product about a stored toolset-bound token, what it records on the connection, and the rules it
 * judges from that record.
 *
 * A toolset-bound token is opaque to WayAI. The one place it says what it reaches is its own
 * toolset resolve, `GET /v1/_by_token/toolsets/{slug}/resolved`, which answers the expanded
 * toolset, the base it serves (`base_id`), that base's environment and its current incarnation
 * (`base_generation`), and the token's own binding (`toolset_binding`). Every write that stores
 * a toolset connection's token resolves it there and records the result as the connection's
 * server-owned `binding` setting; every rule below is judged from that record, so the writes
 * that change which agents use the connection need no call to the data product.
 */

import { z } from 'zod';
import { CUSTOMER_FACING_AGENT_ROLES } from '../constants';
import { REKOR_V1_PREFIX } from './rekor-attestation';

// =============================================
// Connection settings
// =============================================

/** The setting naming the toolset: its slug, as in its MCP URL (`…/t/{slug}/mcp`). */
export const TOOLSET_SLUG_SETTING = 'toolset';

/**
 * The SERVER-OWNED setting recording what the stored token resolved to when it was written. No
 * author ever states it: the connector's settings schema does not declare it, so the write path's
 * sanitizer strips any value a caller sends, and the connection-write hook writes it from the
 * resolve. Absent means the connection holds no token, and so reaches nothing.
 */
export const TOOLSET_BINDING_SETTING = 'binding';

/**
 * Settings keys the platform writes and no author states. The config-as-code diff ignores them on
 * both sides and pull never exports them, so a converged `hub.yaml` stays converged.
 */
export const SERVER_OWNED_CONNECTION_SETTINGS: readonly string[] = [TOOLSET_BINDING_SETTING];

/** A copy of `settings` without its server-owned keys — what an author states. */
export function withoutServerOwnedSettings(settings: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(settings).filter(([key]) => !SERVER_OWNED_CONNECTION_SETTINGS.includes(key)));
}

const baseEnvironmentSchema = z.enum(['preview', 'production']);

/**
 * What a toolset exposes, reduced to the three facts the customer-facing rule reads
 * (`toolsetBindingAllowsCustomerFacing`).
 */
export const toolsetSurfaceSchema = z.object({
  /** Any record, composite or relationship tool: the toolset reaches records. */
  records: z.boolean(),
  sql_query: z.boolean(),
  batch: z.boolean(),
});

export type ToolsetSurface = z.infer<typeof toolsetSurfaceSchema>;

export const toolsetConnectionBindingSchema = z.object({
  base_id: z.string().min(1),
  /** The slug the token was resolved against — `settings.toolset` at that write. */
  toolset: z.string().min(1),
  /** The base's incarnation when the token was written; a bound attested call states it. */
  base_generation: z.number().int().min(1),
  environment: baseEnvironmentSchema,
  surface: toolsetSurfaceSchema,
});

export type ToolsetConnectionBinding = z.infer<typeof toolsetConnectionBindingSchema>;

/**
 * The binding a connection's settings record, or null when they record none, or one that does not
 * parse. Accepts the settings as stored (a JSON string) or decoded.
 */
export function readToolsetBinding(settings: unknown): ToolsetConnectionBinding | null {
  let decoded: unknown = settings;
  if (typeof settings === 'string') {
    try {
      decoded = JSON.parse(settings);
    } catch {
      return null;
    }
  }
  if (!decoded || typeof decoded !== 'object') return null;
  const parsed = toolsetConnectionBindingSchema.safeParse((decoded as Record<string, unknown>)[TOOLSET_BINDING_SETTING]);
  return parsed.success ? parsed.data : null;
}

// =============================================
// The resolve WayAI reads
// =============================================

/**
 * The data product's charset and cap for a toolset slug, which it interpolates into its paths
 * (`STRUCTURAL_ID_RE` and `SLUG_ID_MAX_LENGTH` on the far side). A slug outside it names no
 * toolset, so it is refused by name before any call and never spliced into a URL.
 */
export const TOOLSET_SLUG_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;

/** The toolset resolve a stored token reaches, below the data service's origin. */
export function rekorToolsetResolvePath(slug: string): string {
  return `${REKOR_V1_PREFIX}/_by_token/toolsets/${encodeURIComponent(slug)}/resolved`;
}

/** A stored token's toolset tool definitions (MCP `tools/list` shape), below the data service's origin. */
export function rekorToolsetToolsPath(slug: string): string {
  return `${REKOR_V1_PREFIX}/_by_token/toolsets/${encodeURIComponent(slug)}/tools`;
}

/** The part of the tool definitions this side reads: each tool's name (the rest is dropped). */
export const rekorToolsetToolsSchema = z.object({
  data: z.object({
    tools: z.array(z.object({ name: z.string() })),
  }),
});

/**
 * The data product's error code for a base whose id was destroyed and claimed again since the
 * caller's incarnation (`BASE_INCARNATION_MISMATCH_CODE` on the far side): a 409.
 */
export const REKOR_BASE_INCARNATION_MISMATCH = 'BASE_INCARNATION_MISMATCH';

/**
 * The part of the toolset resolve this side reads. Deliberately not `.strict()`, and passing
 * unknown keys through: the producer is the data product, on its own deploy cadence, and the tool
 * arrays are its own shapes — only their presence is read here, and a key this side does not know
 * is judged by `summarizeToolsetSurface` rather than dropped.
 */
export const rekorResolvedToolsetSchema = z.object({
  data: z.object({
    base_id: z.string().min(1),
    environment: baseEnvironmentSchema,
    base_generation: z.number().int().min(1),
    /**
     * The calling token's binding, or null for a token bound to none. A bound token can call only
     * its toolset's tools, which is what a toolset connection stores (#5083 decision 14). Its org
     * is the token's own: base ids are unique only per org, so the base id alone does not say
     * which base the token reaches.
     */
    toolset_binding: z.object({
      org_id: z.string().optional(),
      base_id: z.string(),
      toolset_id: z.string(),
    }).nullable().optional(),
    actions: z.array(z.unknown()).optional(),
    composites: z.array(z.unknown()).optional(),
    relationships: z.array(z.unknown()).optional(),
    files: z.array(z.unknown()).optional(),
    batch: z.object({ enabled: z.boolean().optional() }).passthrough().nullable().optional(),
    sql_query: z.boolean().optional(),
  }).passthrough(),
});

export type RekorResolvedToolset = z.infer<typeof rekorResolvedToolsetSchema>['data'];

/**
 * The resolve's keys that are not tools: the toolset's own description, its expanded record-type
 * schemas (present only beside the record tools that need them), and what the data product reports
 * about the base and the caller. With the tool keys `summarizeToolsetSurface` classifies, these are
 * every key the resolve carries today.
 */
const RESOLVE_DESCRIPTIVE_KEYS: ReadonlySet<string> = new Set([
  'id', 'name', 'description', 'created_at', 'updated_at', 'record_types',
  'base_id', 'environment', 'base_generation', 'toolset_binding',
]);

/** The tool keys `summarizeToolsetSurface` classifies by name. */
const RESOLVE_TOOL_KEYS: ReadonlySet<string> = new Set([
  'actions', 'composites', 'relationships', 'files', 'batch', 'sql_query',
]);

/** Whether a resolve value exposes anything: a non-empty list, `true`, or any other non-empty value. */
function exposesSomething(value: unknown): boolean {
  if (Array.isArray(value)) return value.length > 0;
  if (value === null || value === undefined || value === false || value === '') return false;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
}

/**
 * Reduce a resolved toolset to the facts the customer-facing rule reads. A key the resolve carries
 * that this side neither describes nor classifies counts as reaching records when it exposes
 * anything: a kind of tool the data product adds later is refused to a customer-facing agent until
 * it is classified here, rather than admitted unread.
 */
export function summarizeToolsetSurface(data: RekorResolvedToolset): ToolsetSurface {
  const unclassified = Object.entries(data).some(
    ([key, value]) => !RESOLVE_DESCRIPTIVE_KEYS.has(key) && !RESOLVE_TOOL_KEYS.has(key) && exposesSomething(value),
  );
  return {
    records: exposesSomething(data.actions) || exposesSomething(data.composites) || exposesSomething(data.relationships) || unclassified,
    sql_query: data.sql_query === true,
    batch: data.batch?.enabled === true,
  };
}

/**
 * Whether the resolving token is bound to exactly this toolset, on the base it resolved, in this
 * org. The org is what makes the recorded base the one a call attested for the hub's org reaches:
 * another org can hold a base and toolset of the same ids, so a binding whose org the token does
 * not report is not admitted.
 */
export function resolvedToolsetIsBound(data: RekorResolvedToolset, slug: string, orgId: string): boolean {
  const binding = data.toolset_binding;
  return !!binding
    && binding.org_id === orgId
    && binding.base_id === data.base_id
    && binding.toolset_id === slug;
}

// =============================================
// The customer-facing rule (#5083 decisions 12 and 13)
// =============================================

/** Whether `role` is customer-facing (`CUSTOMER_FACING_AGENT_ROLES`, `constants.ts`). */
export function isCustomerFacingAgentRole(role: unknown): boolean {
  return typeof role === 'string' && (CUSTOMER_FACING_AGENT_ROLES as readonly string[]).includes(role);
}

/**
 * May a customer-facing agent use a connection with this binding? Until customer-scoped tokens
 * (RK2), only a toolset of file tools alone: no record, composite or relationship tools, and
 * neither `sql_query` nor `batch`, which get around field limits. A connection with no binding
 * holds no token and reaches nothing, so it is admitted.
 */
export function toolsetBindingAllowsCustomerFacing(binding: ToolsetConnectionBinding | null | undefined): boolean {
  return bindingBeyondFiles(binding) === null;
}

/** The binding when it reaches more than files — what a customer-facing agent may not use — else null. */
export function bindingBeyondFiles(binding: ToolsetConnectionBinding | null | undefined): ToolsetConnectionBinding | null {
  if (!binding) return null;
  const { records, sql_query, batch } = binding.surface;
  return records || sql_query || batch ? binding : null;
}

// =============================================
// The HubDO chokepoint's refusals
// =============================================

/** A customer-facing agent and a toolset that reaches more than files, meeting on one write. */
export const TOOLSET_CUSTOMER_FACING_REFUSAL =
  'A customer-facing agent (a pilot, pilot specialist or pilot advisor) can use a toolset only when it exposes files alone: no record, composite or relationship tools, and neither sql_query nor batch';

/** A toolset connection recording a base of the other environment. */
export const TOOLSET_ENVIRONMENT_REFUSAL =
  "A toolset connection can hold only a token for a base in its hub's own environment";

/**
 * A toolset connection's token written without the binding resolved from it, or a binding left
 * behind with no token. Only a write path that skipped the connection-write hook can produce it,
 * so it is a fault, never the author's 4xx: `toolsetConnectionRefusalMessage` does not match it.
 */
export const TOOLSET_UNRESOLVED_CREDENTIAL_REFUSAL =
  "A toolset connection's token can be stored only together with the binding resolved from it";

/**
 * The author-facing refusal `err` carries — its whole message, which names what met — or null.
 * The chokepoint's refusals cross the RPC boundary as plain `Error`s, so an ingress matches them
 * by these sentences and answers a 4xx rather than an internal fault.
 */
export function toolsetConnectionRefusalMessage(err: unknown): string | null {
  if (!(err instanceof Error)) return null;
  const { message } = err;
  return message.includes(TOOLSET_CUSTOMER_FACING_REFUSAL) || message.includes(TOOLSET_ENVIRONMENT_REFUSAL)
    ? message
    : null;
}
